import React, { useState, useEffect, useRef, useCallback } from 'react';
import './InputField.scss';

const InputField = ({
  type = 'text',
  label,
  value: controlledValue,
  onChange,
  onFocus,
  onBlur,
  placeholder = '',
  required = false,
  error = '',
  className = '',
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword = null,
  isBrightMode = false,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(controlledValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const inputRef = useRef(null);
  const isControlled = controlledValue !== undefined && controlledValue !== null;

  // Sync internal state with controlled value
  useEffect(() => {
    if (isControlled && controlledValue !== internalValue) {
      console.log('🔵 [InputField] Syncing internal value from controlled:', {
        controlledValue,
        internalValue,
        willUpdate: true
      });
      setInternalValue(controlledValue);
    }
  }, [controlledValue, isControlled]);

  // State machine: idle -> typing -> changed
  const [state, setState] = useState('idle'); // 'idle' | 'typing' | 'changed'

  const handleInput = useCallback((e) => {
    const newValue = e.target.value;
    console.log('🔵 [InputField] handleInput fired:', {
      state,
      oldValue: internalValue,
      newValue: newValue,
      eventType: e.type,
      isControlled,
      timestamp: Date.now()
    });

    // Update internal state
    setInternalValue(newValue);
    setHasChanged(true);
    setState('typing');

    // If controlled, call onChange
    if (isControlled && onChange) {
      console.log('🔵 [InputField] Calling onChange (controlled):', newValue);
      onChange(e);
    } else if (!isControlled) {
      // Uncontrolled mode - update internal state only
      console.log('🔵 [InputField] Updating internal state (uncontrolled):', newValue);
    }

    // Transition to 'changed' state after a delay
    setTimeout(() => {
      setState('changed');
    }, 100);
  }, [internalValue, state, isControlled, onChange]);

  const handleChange = useCallback((e) => {
    console.log('🔵 [InputField] handleChange fired:', {
      state,
      value: e.target.value,
      eventType: e.type,
      timestamp: Date.now()
    });
    // onChange is already handled in handleInput, but we'll call it here too for compatibility
    if (onChange) {
      onChange(e);
    }
  }, [state, onChange]);

  const handleFocus = useCallback((e) => {
    console.log('🔵 [InputField] handleFocus fired:', {
      state,
      value: e.target.value,
      timestamp: Date.now()
    });
    setIsFocused(true);
    setState('typing');
    if (onFocus) {
      onFocus(e);
    }
  }, [state, onFocus]);

  const handleBlur = useCallback((e) => {
    console.log('🔵 [InputField] handleBlur fired:', {
      state,
      value: e.target.value,
      timestamp: Date.now()
    });
    setIsFocused(false);
    // Don't reset to 'idle' state on blur - keep the 'changed' state if user has typed
    if (hasChanged) {
      setState('changed');
    } else {
      setState('idle');
    }
    if (onBlur) {
      onBlur(e);
    }
  }, [state, onBlur, hasChanged]);

  const handleKeyDown = useCallback((e) => {
    console.log('🔵 [InputField] handleKeyDown:', {
      key: e.key,
      valueBefore: e.target.value,
      state,
      timestamp: Date.now()
    });
  }, [state]);

  const handleKeyUp = useCallback((e) => {
    console.log('🔵 [InputField] handleKeyUp:', {
      key: e.key,
      valueAfter: e.target.value,
      state,
      timestamp: Date.now()
    });
  }, [state]);

  // Use internal value if uncontrolled, controlled value if controlled
  const displayValue = isControlled ? controlledValue : internalValue;
  
  // Determine input type - if password toggle is enabled, switch between 'password' and 'text'
  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password')
    : type;

  return (
    <div className={`InputField ${className} ${showPasswordToggle ? 'InputField--password-toggle' : ''} ${isBrightMode ? 'InputField--bright' : ''}`}>
      {label && (
        <label className="InputField__label">
          {label} {required && <span className="InputField__required">*</span>}
        </label>
      )}
      <div className="InputField__input-wrapper">
        <input
          ref={inputRef}
          type={inputType}
          className={`InputField__input ${error ? 'InputField__input--error' : ''} InputField__input--${state} ${showPasswordToggle ? 'InputField__input--with-toggle' : ''}`}
          value={displayValue}
          onChange={handleChange}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onClick={(e) => {
            // Ensure input gets focus when clicking on it (not the button)
            if (e.target === inputRef.current) {
              inputRef.current.focus();
            }
            console.log('🔵 [InputField] onClick:', {
              value: e.target.value,
              readOnly: e.target.readOnly,
              disabled: e.target.disabled,
              pointerEvents: window.getComputedStyle(e.target).pointerEvents,
              userSelect: window.getComputedStyle(e.target).userSelect,
              state
            });
          }}
          placeholder={placeholder}
          required={required}
          {...props}
        />
        {showPasswordToggle && (
          <button
            type="button"
            className="InputField__password-toggle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onTogglePassword) {
                onTogglePassword(!showPassword);
              }
            }}
            onMouseDown={(e) => {
              // Prevent input focus when clicking the button
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              // Prevent input focus on touch devices
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.82L19.56 16.74C21.07 15.49 22.26 13.86 23 12C21.27 7.61 17 4.5 12 4.5C10.6 4.5 9.26 4.75 8.04 5.2L10.17 7.33C10.74 7.12 11.35 7 12 7ZM2 4.27L4.28 6.55L4.73 7L7.53 9.8C6.08 10.81 5 12.5 5 14.5C5 17.26 7.24 19.5 10 19.5C11.5 19.5 12.81 18.92 13.8 18L16.74 20.94C15.49 21.07 13.86 22.26 12 23C7 23 2.73 19.89 1 15.5C1.69 13.24 2.79 11.15 4.19 9.4L2 7.21V4.27ZM9.53 11.8L11.73 14C11.5 14.07 11.26 14.12 11 14.12C9.62 14.12 8.5 13 8.5 11.62C8.5 11.36 8.55 11.12 8.62 10.89L9.53 11.8ZM15.97 18.27L18.73 21.03L20.15 19.61L4.39 3.85L2.97 5.27L5.73 8.03C4.08 9.07 3 10.84 3 13C3 15.76 5.24 18 8 18C9.84 18 11.43 17.17 12.54 16L15.97 18.27Z" fill="currentColor"/>
              </svg>
            )}
          </button>
        )}
      </div>
      {error && (
        <div className="InputField__error">{error}</div>
      )}
    </div>
  );
};

export default InputField;

